import { RoutingLink } from "#batteries-included-components/RoutingLink";
import { useListCalculators } from "#hooks/adapters/useCalculators";
import {
  useBulkApplyConfigurationsAndRunCalcsAsync,
  useListEstimationMethodConfiguration,
} from "#hooks/adapters/useEstimationMethods";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { ImportDataAction } from "#src/batteries-included-components";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import {
  CALCULATIONS_FILTER_CONFIG,
  CalculationsTableFilterArea,
  CalculationsTableTitleDecorationFilterArea,
} from "#src/batteries-included-components/FilterAreas/CalculationsFilterAreas";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import { useStickyState } from "#src/hooks/useStickyState";
import { useExportList } from "#src/components/hooks/useExportList";
import {
  CalculatorStatusRawType,
  rawToSimpleStatus,
  simpleToRawStatuses,
} from "#utils/calculatorStatus";
import { getIntervalInMonthsFormatted } from "#utils/date";
import {
  linkToCalculationDetailPage,
  linkToCalculationResultDetailPage,
} from "#utils/links";
import { NetworkCalcsMeasurementTypes } from "#utils/networkCalculationMeasurementTypeKeys";
import {
  Button,
  DataTable,
  DataTablePanel,
  Dialog,
  HeaderType,
  Icon,
  ItemAction,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  CalculatorVersionIncludeFields,
  EstimationMethodAdapter,
  EstimationMethodWithConfigurationAndRunType,
  MeasurementTypeType,
  SortDirection,
} from "@validereinc/domain";
import {
  monthFormatter,
  toFlattenedObject,
  toStartCaseString,
  toTitleCaseString,
  yearMonthParser,
} from "@validereinc/utilities";
import { uniqBy } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import {
  ASYNC_THRESHOLD,
  getFacilityKey,
  toTableKey,
  useAssetSpecificCalculationHeaders,
  useCalculationInputHeaders,
  useCalculationOutputHeaders,
  useGetMeasurementTypesFromCalculatorResultsBasedOnReportingGroup,
  withTableKey,
} from "./CalculationsTablePanel.helper";

const formatStatusFilter = (status: string | string[]) =>
  Array.isArray(status) ? status : { $exact: status };

export const CalculationsTablePanel = ({
  assetType,
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  calculationJobStorageKey,
  defaultMeasurementTypes = [],
  showEstimationMethodStatusFilter,
  measurementTypeFilter = () => true,
}: {
  assetType: AssetTypeType;
  calculationJobStorageKey: string;
  defaultMeasurementTypes?: string[];
  showEstimationMethodStatusFilter?: boolean;
  measurementTypeFilter: (measurementType: MeasurementTypeType) => boolean;
} & Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "viewConfigStorageKey"
  | "tableConfigStorageKey"
  | "tableStateStorageKey"
>) => {
  const { measurementTypes, isLoading: isMeasurementTypesLoading } =
    useMeasurementTypes();
  const [selected, onSelectionChange] = useState<
    Record<string, EstimationMethodWithConfigurationAndRunType>
  >({});
  const [showBulkConfigureAndRunWarning, setShowBulkConfigureAndRunWarning] =
    useState(false);
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [viewFilters] = useStickyState({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);

  const filters = useMemo<Record<string, any>>(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "year_month",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );
  const {
    [CALCULATIONS_FILTER_CONFIG.equipmentFacilityStatus.name]:
      equipmentFacilityStatus,
    [CALCULATIONS_FILTER_CONFIG.equipmentStatus.name]: equipmentStatus,
    [CALCULATIONS_FILTER_CONFIG.flowFacilityStatus.name]: flowFacilityStatus,
    [CALCULATIONS_FILTER_CONFIG.flowEquipmentStatus.name]: flowEquipmentStatus,
    [CALCULATIONS_FILTER_CONFIG.flowStatus.name]: flowStatus,
    [CALCULATIONS_FILTER_CONFIG.networkStatus.name]: networkStatus,
    [CALCULATIONS_FILTER_CONFIG.status.name]: statusFilter,
  } = toFlattenedObject(filters);
  const {
    [CALCULATIONS_FILTER_CONFIG.reportingScenario.name]: reportingGroup,
    [CALCULATIONS_FILTER_CONFIG.facility.name]: facilityId,
    [CALCULATIONS_FILTER_CONFIG.dateRange.name]: dateRange,
    [CALCULATIONS_FILTER_CONFIG.estimationMethodStatus.name]:
      estimationMethodStatus,
    ...restFilters
  } = filters;
  /** We shouldn't need to apply defaults here, but sometimes a race condition overwrites the default date */
  const year_month = getIntervalInMonthsFormatted(
    !!dateRange?.from && !!dateRange?.to
      ? {
          from: new Date(dateRange?.from),
          to: new Date(dateRange?.to),
        }
      : DEFAULT_DATE_RANGES.lastWholeMonth
  );
  const calculationsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      ...toFlattenedObject(restFilters),
      ...(facilityId ? { [getFacilityKey(assetType)]: facilityId } : {}),
      ...(statusFilter
        ? {
            status: simpleToRawStatuses(statusFilter as any),
          }
        : {}),
      ...(equipmentFacilityStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.equipmentFacilityStatus.name]:
              formatStatusFilter(equipmentFacilityStatus as string | string[]),
          }
        : {}),
      ...(equipmentStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.equipmentStatus.name]:
              formatStatusFilter(equipmentStatus as string | string[]),
          }
        : {}),
      ...(flowFacilityStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.flowFacilityStatus.name]:
              formatStatusFilter(flowFacilityStatus as string | string[]),
          }
        : {}),
      ...(flowEquipmentStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.flowEquipmentStatus.name]:
              formatStatusFilter(flowEquipmentStatus as string | string[]),
          }
        : {}),
      ...(flowStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.flowStatus.name]: formatStatusFilter(
              flowStatus as string | string[]
            ),
          }
        : {}),
      ...(networkStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.networkStatus.name]: formatStatusFilter(
              networkStatus as string | string[]
            ),
          }
        : {}),
      entity_type: { $exact: assetType },
      reporting_group_id: { $exact: reportingGroup },
      year_month,
    },
  };
  // Year month is separated out for the async bulk configure
  const bulkConfigureFilters = {
    filters: {
      ...calculationsParams.filters,
      year_month: undefined,
      id: undefined as string[] | undefined,
    },
    year_months: year_month,
    promote_to_record: assetType !== AssetType.EQUIPMENT,
  };

  const calculatorQuery = useListCalculators({
    include: [CalculatorVersionIncludeFields.calculation_parameters],
  });
  const calculatorLookup = useMemo(
    () =>
      Object.fromEntries(
        calculatorQuery.data?.calculators?.map(
          ({ id, default_version, versions }) => [
            id,
            versions.find(({ version }) => version === default_version),
          ]
        ) ?? []
      ),
    [calculatorQuery.data]
  );

  const {
    measurementTypeList,
    isLoading:
      isMeasurementTypesFromCalculatorResultsBasedOnReportingGroupLoading,
  } = useGetMeasurementTypesFromCalculatorResultsBasedOnReportingGroup(
    reportingGroup,
    defaultMeasurementTypes
  );
  const {
    headers: calculatorInputHeaders,
    isLoading: isCalculatorInputHeadersLoading,
  } = useCalculationInputHeaders(
    calculatorLookup[filters[CALCULATIONS_FILTER_CONFIG.calculatorName.name]]
      ?.calculation_parameters
  );
  const volumeAllowed = ["volume"];
  const networkAllowed = [...NetworkCalcsMeasurementTypes];
  const emissionsAllowed = ["mass_n2o", "mass_co2", "mass_ch4", "mass_co2eq"];
  const measurementKeys = [
    ...defaultMeasurementTypes,
    ...measurementTypeList.filter((key) => {
      const measurementType = measurementTypes.find(({ id }) => id === key);
      return (
        measurementType &&
        measurementTypeFilter(measurementType) &&
        !defaultMeasurementTypes.includes(key)
      );
    }),
  ];

  const {
    headers: calculationOutputHeaders,
    isLoading: isCalculationOutputHeadersLoading,
  } = useCalculationOutputHeaders({
    measurementKeys,
    volumeAllowed,
    networkAllowed,
    emissionsAllowed,
    assetType,
  });

  const calculationsQuery = useListEstimationMethodConfiguration({
    ...calculationsParams,
    filters: {
      ...calculationsParams.filters,
      ...(estimationMethodStatus
        ? {
            [CALCULATIONS_FILTER_CONFIG.estimationMethodStatus.name]:
              formatStatusFilter(estimationMethodStatus),
          }
        : {}),
    },
  });

  const applyConfigurationBulkAndRunAsync =
    useBulkApplyConfigurationsAndRunCalcsAsync(calculationJobStorageKey);

  const { mutate: handleExport, isLoading: isExporting } = useExportList({
    exportFn: () =>
      EstimationMethodAdapter.configuration.exportList({
        ...calculationsParams,
      }),
  });

  const onClickCalculate = () => {
    const configurationCount = Object.keys(selected).length || totalCount || 0;
    if (configurationCount > ASYNC_THRESHOLD) {
      setShowBulkConfigureAndRunWarning(true);
    } else {
      onApplyConfigurationAndRun(selected);
    }
  };

  const onApplyConfigurationAndRun = (
    selectedEstimationMethods: Record<
      string,
      EstimationMethodWithConfigurationAndRunType
    >
  ) => {
    const hasSelection = Object.keys(selectedEstimationMethods).length > 0;
    const filtersToSend = {
      ...structuredClone(bulkConfigureFilters),
    };
    if (hasSelection) {
      const selectedEstimationIds = Object.values(
        selectedEstimationMethods
      ).map((item) => item.id);
      const yearsMonths = uniqBy(
        Object.values(selectedEstimationMethods)
          .filter((item) => !!item.calculator_configuration?.year_month)
          .map((item) => item.calculator_configuration!.year_month),
        (s) => s
      );
      filtersToSend.filters = {
        ...structuredClone(filtersToSend.filters),
        id: selectedEstimationIds,
      };
      filtersToSend.year_months = yearsMonths;
    }
    applyConfigurationBulkAndRunAsync.mutate(filtersToSend);
  };

  /** Reset the selection when the filtered set changes */
  useEffect(() => {
    onSelectionChange({});
  }, [calculationsQuery.data?.total_entries]);

  const items =
    calculationsQuery.data?.data.map(withTableKey) ??
    ([] as EstimationMethodWithConfigurationAndRunType[]);
  const assetSpecificHeaders = useAssetSpecificCalculationHeaders(
    assetType,
    items,
    year_month
  );

  const shouldShowInputs =
    !!filters[CALCULATIONS_FILTER_CONFIG.calculatorName.name];
  const headers: Array<
    HeaderType<EstimationMethodWithConfigurationAndRunType>
  > = [
    {
      key: "year_month",
      label: "Period",
      isSortable: true,
      renderComponent: ({ item }) => {
        const yearMonth = item?.calculator_configuration?.year_month;
        const pathname = linkToCalculationDetailPage(
          assetType,
          item.entity_id,
          item.id
        );
        return (
          <RoutingLink to={pathname}>
            {yearMonth && monthFormatter(yearMonthParser(yearMonth))}
          </RoutingLink>
        );
      },
    },
    ...assetSpecificHeaders.headers,
    ...(assetType !== AssetType.ASSET_GROUP
      ? [
          {
            key: "estimation_method_details",
            label: "Estimation Method Details",
            headers: [
              {
                key: "name",
                label: "Estimation Method",
                isSortable: true,
                renderComponent: ({
                  item,
                }: {
                  item: EstimationMethodWithConfigurationAndRunType;
                }) => {
                  const pathname = linkToCalculationDetailPage(
                    assetType,
                    item.entity_id,
                    item.id
                  );
                  return <RoutingLink to={pathname}>{item.name}</RoutingLink>;
                },
              },
              {
                key: "analytics_calculator_id",
                label: "Calculator Name",
                isSortable: true,
                renderComponent: ({
                  item,
                }: {
                  item: EstimationMethodWithConfigurationAndRunType;
                }) => (
                  <DataTable.DataRow.TextCell
                    value={
                      calculatorLookup[item?.analytics_calculator_id]?.title ??
                      toStartCaseString(item?.analytics_calculator_id)
                    }
                  />
                ),
              },
            ],
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      isSortable: true,
      renderComponent: ({
        item,
      }: {
        item: EstimationMethodWithConfigurationAndRunType;
      }) => {
        const status = rawToSimpleStatus(
          item.status as CalculatorStatusRawType
        );

        return (
          <Pill variant={status?.variant}>
            {toTitleCaseString(status?.name, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        );
      },
    },
    ...(shouldShowInputs ? calculatorInputHeaders : []),
    ...(assetType !== AssetType.ASSET_GROUP
      ? [
          {
            key: "result",
            label: "Result",
            renderComponent: ({
              item,
            }: {
              item: EstimationMethodWithConfigurationAndRunType;
            }) => {
              if (!(item.calculator_run as any)?.run_id) {
                return "-";
              }
              const yearMonth = item?.calculator_configuration?.year_month;
              const pathname = linkToCalculationResultDetailPage(
                assetType,
                item.entity_id,
                item.id,
                yearMonth ?? ""
              );
              return (
                <RoutingLink to={pathname}>
                  {yearMonth && monthFormatter(yearMonthParser(yearMonth))}
                </RoutingLink>
              );
            },
          },
        ]
      : []),
    ...calculationOutputHeaders,
  ];

  const totalCount = calculationsQuery.data?.total_entries;
  const calculationCount = Object.keys(selected).length || totalCount || 0;
  const isLoading =
    assetSpecificHeaders.isLoading ||
    calculatorQuery.isLoading ||
    calculationsQuery.isLoading ||
    isMeasurementTypesLoading ||
    isMeasurementTypesFromCalculatorResultsBasedOnReportingGroupLoading ||
    isCalculatorInputHeadersLoading ||
    isCalculationOutputHeadersLoading;

  const actionRow = [
    isDataIngestionEnabled ? (
      <ImportDataAction
        key="import-calculations"
        resource={{
          id: "estimation_method_config",
          description: "",
          label: {
            singular:
              assetType === AssetType.EQUIPMENT
                ? "Emissions Calculation"
                : assetType === AssetType.FLOW
                  ? "Volumetric Calculation"
                  : "Network Calculation",
            plural:
              assetType === AssetType.EQUIPMENT
                ? "Emissions Calculations"
                : assetType === AssetType.FLOW
                  ? "Volumetric Calculations"
                  : "Network Calculations",
          },
        }}
      />
    ) : null,
    <PermissionAwareExportButton
      key="export-calculations-records"
      onClick={handleExport}
      isExporting={isExporting}
    />,
    <Button
      key="apply_configurations_and_run"
      variant="primary"
      slotLeft={<Icon variant="play-circle" />}
      onClick={onClickCalculate}
      disabled={!totalCount}
      isLoading={applyConfigurationBulkAndRunAsync.isLoading}
    >
      Calculate
    </Button>,
  ];

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          actionRow,
          title: "Calculations",
          titleDecorator: (
            <CalculationsTableTitleDecorationFilterArea
              filterConfigStorageKey={filterConfigStorageKey}
            />
          ),
        }}
        filterComponent={
          <CalculationsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            assetType={assetType}
            showEstimationMethodStatusFilter={showEstimationMethodStatusFilter}
          />
        }
        dataTableProps={{
          headers,
          items,
          isLoading,
          isBusy: calculationsQuery.isFetching,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: totalCount,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          selected,
          onSelectionChange,
          getItemId: toTableKey,
          getItemActions: ({ item }): ItemAction[] => [
            {
              label: "Calculate",
              buttonProps: {
                onClick: () => {
                  onApplyConfigurationAndRun({
                    [item.id]: item,
                  });
                },
                icon: "play-circle",
              },
            },
          ],
        }}
      />
      <Dialog
        key="initiate-large-bulk-run-dialog"
        title="Initiate Large Bulk Run?"
        isOpen={showBulkConfigureAndRunWarning}
        onClose={() => setShowBulkConfigureAndRunWarning(false)}
        actionRow={[
          <Button
            key="bulk-configure-and-action"
            variant="primary"
            onClick={() => {
              onApplyConfigurationAndRun(selected);
              setShowBulkConfigureAndRunWarning(false);
            }}
          >
            Run Calculations
          </Button>,
        ]}
      >
        You have selected to run all {calculationCount}{" "}
        {Object.keys(selected).length ? "selected" : "filtered"} calculations.
        This may take some time.
      </Dialog>
    </>
  );
};
